import { useEffect, useState, useMemo, useCallback } from "react";
import "instantsearch.css/themes/satellite.css";
import "./SupplierInventorySearch.css";
import "./RefinementList.css";
import {
  Text,
  Box,
  Flex,
  Button,
  Icon,
  SimpleGrid,
  Container,
  Badge,
} from "@chakra-ui/react";
import { MdLocationOn } from "react-icons/md";
import { BsEyeFill } from "react-icons/bs";
import { FaUserCheck } from "react-icons/fa";
import { fetchTopSupplierCandidates } from "./utils/typesenseUtils";
import {
  buildSupplierMetrics,
  SupplierMetrics,
} from "./utils/supplierMetricsUtils";
import { OfflineSupplierEntry } from "./types";
import {
  createSyntheticTopHit,
  mergeOfflinePartsIntoHits,
  isTopQualifiedFromParts,
} from "./utils/offlinePartAvailabilityUtils";
import { applySupplierInventoryDeltasToParts } from "./utils/supplierInventoryDeltaUtils";
import { FaTrophy } from "react-icons/fa";
import { SupplierText } from "./SupplierBadge";
import { GaraazRankMedals } from "@svan-garaaz/reactsharedlib";
import { RequestPartConfirmationButton } from "./RequestPartConfirmationButton";

interface Hit {
  group_key: string[];
  hits: any[];
  found: number;
}

interface TopFulfilledSupplierListProps {
  handleSupplierClick: (groupKey: string, isFromTopSuppliers: boolean) => void;
  onRequestPartConfirmation?: (groupKey: string) => void;
  setIsTopSuppliers: (count: number) => void;
  customerSupplierAssociations: string[];
  preFacetedPartNumbers: any[];
  handleSelectListItem: (
    item: { _id: string; supplierName: string },
    supplierParts?: any[],
  ) => void;
  handleAddToSelectedSupplierBucket: (item: any, supplierParts?: any[]) => void;
  customerGeoPoint: { latitude: number; longitude: number };
  onSelectionClose: () => void;
  setTopSupplierHitMap: (map: Map<string, any[]>) => void;
  typesenseInstantsearchAdapter: any;
  supplierDiscoveryCollectionName: string;
  pinnedSupplierIds: string[];
  preFacetedPartNumbersMap: Map<string, number>;
  totalRequestedQuantity: number;
  hits: Hit[];
  topSupplierHitMap: Map<string, any[]>;
  supplierCityMap: Map<string, string>;
  isEstimateOrder: boolean;
  offlineOnlyTopGroupKeys?: Set<string>;
}

interface EliteSupplierCardProps {
  metrics: SupplierMetrics;
  index: number;
  onSelect: () => void;
  onAllocate: () => void;
  onButtonClick: (e: any) => void;
  onRequestPartConfirmation: (e: any) => void;
  isEstimateOrder: boolean;
}

const getFinalScore = (metrics: any) => {
  const fulfilled = metrics.fulfilledPercent ?? 0;
  const quantity = metrics.totalPartPercentage ?? 0;
  const coverage = metrics.coverage ?? 0;
  const raw = fulfilled + quantity + coverage;
  const percentage = Math.min(Math.round((raw / 300) * 100), 100);
  return percentage;
};

const amber100 = "#fde68a";
const amber200 = "#fcd34d";
const amber50 = "#fef3c7";
const amber600 = "#d97706";
const amber700 = "#b45309";
const trophyGold = "#F59E0B";

const EliteSupplierCard: React.FC<EliteSupplierCardProps> = ({
  metrics,
  index,
  onSelect,
  onAllocate,
  onButtonClick,
  onRequestPartConfirmation,
  isEstimateOrder,
}) => {
  const finalScore = getFinalScore(metrics);
  return (
    <Flex
      flexDirection="column"
      justifyContent="space-between"
      bgGradient={`linear(to-br, white, ${amber50})`}
      border="1px solid"
      borderColor={amber100}
      _hover={{ borderColor: amber200 }}
      borderRadius="xl"
      p={{ base: 4, md: 4 }}
      transition="all 0.3s"
      onClick={onSelect}
      cursor="pointer"
      boxShadow="0 0 0.2rem rgba(255, 191, 0, 0.4)"
    >
      <Flex
        direction={{ base: "column", sm: "row" }}
        justify="space-between"
        align={{ base: "flex-start", sm: "flex-start" }}
        gap={3}
        width="100%"
      >
        <Flex
          direction="column"
          align="flex-start"
          gap={0}
          height="100%"
          flex="1"
          minWidth={0}
        >
          <Flex justifyContent="flex-start" gap={1} width="100%">
            <Box maxW="100%" minWidth={0}>
              <SupplierText textStyle="title">
                {metrics.supplierName}
              </SupplierText>
            </Box>

            {index < 3 && (
              <GaraazRankMedals rank={(index + 1) as 1 | 2 | 3} size={18} />
            )}
          </Flex>
          <Flex
            align="center"
            color="gray.500"
            gap={0.5}
            fontWeight="bold"
            fontSize="xs"
            wrap="wrap"
            marginLeft={-0.5}
          >
            <Icon
              as={MdLocationOn}
              boxSize={4}
              color={
                metrics.distanceKm !== undefined &&
                metrics.distanceKm !== Infinity
                  ? "#3b82f6"
                  : "#E40133"
              }
            />
            <SupplierText textStyle="subtitle">
              {metrics.distanceKm !== undefined &&
              metrics.distanceKm !== Infinity
                ? `${metrics.distanceKm.toFixed(1)} km away`
                : "Unavailable"}
            </SupplierText>
          </Flex>
          <Flex
            mt={1}
            align="center"
            color="gray.500"
            gap={1}
            fontWeight="bold"
            fontSize="xs"
            wrap="wrap"
          >
            {metrics.isAssociated && (
              <Badge
                px={2}
                py={0.5}
                fontSize="xs"
                fontWeight="bold"
                borderRadius="full"
                bg="white"
                border="1px solid"
                borderColor={amber100}
                color={amber700}
                textTransform="none"
                boxShadow="0 0 0.1rem rgba(255, 191, 0, 0.4)"
              >
                Associated
              </Badge>
            )}
          </Flex>
        </Flex>

        <Box
          px={{ base: 4, md: 4 }}
          py={{ base: 2, md: 2 }}
          bg={amber50}
          border="1px solid"
          borderColor={amber100}
          borderRadius="xl"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          w={{ base: "100%", sm: "auto" }}
          mt={{ base: 4, sm: 0 }}
          bgGradient={`linear(to-br, white, ${amber50})`}
          boxShadow="0 0 0.1rem rgba(255, 191, 0, 0.4)"
          flexShrink={0}
        >
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="extrabold"
            color={amber600}
          >
            {finalScore}%
          </Text>
          <Text fontSize="xs" fontWeight="bold" color={amber700}>
            Overall Score
          </Text>
        </Box>
      </Flex>

      <Flex mt={4} gap={2}>
        
        {!isEstimateOrder && (<Button
          flex="1"
          py={2}
          bg={amber600}
          color="white"
          borderRadius="md"
          transition="all 0.2s"
          transform="scale(1)"
          _hover={{
            bg: amber700,
            transform: "scale(1.01)",
          }}
          fontSize={{
            base: "xs",
            "2xl": "md",
          }}
          fontWeight="bold"
          leftIcon={<Icon as={FaUserCheck} />}
          onClick={(e) => {
            e.stopPropagation();
            onAllocate();
          }}
        >
          {isEstimateOrder ? "Generate Estimate" : "Allocate"}
        </Button>)}
        <RequestPartConfirmationButton
          isAmber
          supplierId={metrics.supplierId}
          isEstimateOrder={isEstimateOrder}
          onClick={onRequestPartConfirmation}
        />
        <Button
          flex={isEstimateOrder ? 1 : undefined}
          py={2}
          px={3}
          bg={amber600}
          color="white"
          borderRadius="md"
          transition="all 0.2s"
          _hover={{ bg: amber700, transform: "scale(1.01)" }}
          fontSize={{ base: "sm", md: "sm" }}
          fontWeight="bold"
          leftIcon={
            isEstimateOrder ? <Icon as={BsEyeFill} /> : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            onButtonClick(e);
          }}
        >
          {isEstimateOrder ? "View Details" : <Icon as={BsEyeFill} />}
        </Button>
      </Flex>
    </Flex>
  );
};

const TopFulfilledSupplierList = ({
  handleSupplierClick,
  onRequestPartConfirmation,
  setIsTopSuppliers,
  customerSupplierAssociations,
  preFacetedPartNumbers,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  customerGeoPoint,
  onSelectionClose,
  setTopSupplierHitMap,
  typesenseInstantsearchAdapter,
  supplierDiscoveryCollectionName,
  pinnedSupplierIds,
  preFacetedPartNumbersMap,
  totalRequestedQuantity,
  hits,
  topSupplierHitMap,
  supplierCityMap,
  isEstimateOrder,
  offlineOnlyTopGroupKeys,
}: TopFulfilledSupplierListProps) => {
  const userLatitude = customerGeoPoint?.latitude;
  const userLongitude = customerGeoPoint?.longitude;
  const [topSuppliersMetrics, setTopSuppliersMetrics] = useState<
    [string, SupplierMetrics][]
  >([]);

  useEffect(() => {
    let cancelled = false;
    const buildMetrics = async () => {
      const topSuppliersMetricsMap = new Map<string, SupplierMetrics>();

      for (const hit of hits) {
        const groupKey = hit.group_key[0];
        if (!groupKey || topSuppliersMetricsMap.has(groupKey)) continue;

        const [supplierName, supplierId, coords] = groupKey.split("::");

        const metrics = buildSupplierMetrics(
          hit,
          supplierName,
          supplierId,
          coords,
          userLatitude,
          userLongitude,
          customerSupplierAssociations,
          preFacetedPartNumbers,
          preFacetedPartNumbersMap,
          totalRequestedQuantity,
        );

        if (metrics) {
          if (offlineOnlyTopGroupKeys?.has(groupKey)) {
            metrics.distanceKm = 0;
          }
          topSuppliersMetricsMap.set(groupKey, metrics);
        }
      }

      if (!cancelled) {
        setTopSuppliersMetrics(Array.from(topSuppliersMetricsMap.entries()));
        setIsTopSuppliers(topSuppliersMetricsMap.size);
      }
    };

    buildMetrics();
    return () => {
      cancelled = true;
    };
  }, [
    hits,
    customerGeoPoint,
    customerSupplierAssociations,
    preFacetedPartNumbers,
    typesenseInstantsearchAdapter,
    supplierDiscoveryCollectionName,
    pinnedSupplierIds,
    userLatitude,
    userLongitude,
    preFacetedPartNumbersMap,
    totalRequestedQuantity,
    setIsTopSuppliers,
    offlineOnlyTopGroupKeys,
  ]);

  const handleSupplierClickWrapper = useCallback(
    (e: React.MouseEvent, groupKey: string) => {
      e.stopPropagation();
      handleSupplierClick(groupKey, true);
    },
    [handleSupplierClick],
  );

  const handleSelectListItemWrapper = useCallback(
    (metrics: SupplierMetrics, groupKey: string) => {
      handleSelectListItem(
        {
          _id: metrics.supplierId,
          supplierName: metrics.supplierName,
        },
        topSupplierHitMap.get(groupKey),
      );
      onSelectionClose();
    },
    [handleSelectListItem, onSelectionClose, topSupplierHitMap],
  );

  const handleAddToSelectionWrapper = useCallback(
    (
      metrics: SupplierMetrics,
      groupKey: string,
      supplierCityMap: Map<string, string>,
    ) => {
      const supplierCity = supplierCityMap.get(metrics.supplierId);
      handleAddToSelectedSupplierBucket(
        {
          _id: metrics.supplierId,
          supplierName: metrics.supplierName,
          distanceKm: metrics.distanceKm,
          supplierCity,
        },
        topSupplierHitMap.get(groupKey),
      );
    },
    [handleAddToSelectedSupplierBucket, topSupplierHitMap],
  );

  return (
    <>
      {topSuppliersMetrics.length > 0 && (
        <Container
          marginTop={5}
          backgroundColor="#fffdf9"
          maxW="100%"
          paddingTop={4}
          paddingBottom={6}
          paddingLeft={6}
          paddingRight={6}
          borderRadius="lg"
          fontFamily="'Nunito','Inter', sans-serif"
          boxShadow="md"
        >
          <Flex mb={4} justify="space-between" align="center">
            <Box>
              <Flex>
                <Icon
                  as={FaTrophy}
                  mr={2}
                  boxSize={6}
                  alignSelf="center"
                  color={trophyGold}
                />
                <SupplierText textStyle="header">
                  Complete Fullfillment Suppliers
                </SupplierText>
              </Flex>
              <SupplierText textStyle="subtitle">
                Top suppliers with Complete Fulfillment
              </SupplierText>
            </Box>

            <Box backgroundColor={amber50} px={3} py={0.5} borderRadius="3xl">
              <Flex align="center" gap={1}>
                <Icon as={FaTrophy} boxSize={4} color={trophyGold} />
                <Text fontSize="0.8rem" fontWeight="700" color={amber700}>
                  100% Performance Score
                </Text>
              </Flex>
            </Box>
          </Flex>

          <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing={5}>
            {topSuppliersMetrics.map(([groupKey, metrics], index) => (
              <Box key={groupKey}>
                <EliteSupplierCard
                  metrics={metrics}
                  index={index}
                  onSelect={() =>
                    handleAddToSelectionWrapper(
                      metrics,
                      groupKey,
                      supplierCityMap,
                    )
                  }
                  onAllocate={() =>
                    handleSelectListItemWrapper(metrics, groupKey)
                  }
                  onButtonClick={(e) => handleSupplierClickWrapper(e, groupKey)}
                  onRequestPartConfirmation={(e) => {
                    e.stopPropagation();
                    onRequestPartConfirmation?.(groupKey);
                  }}
                  isEstimateOrder={isEstimateOrder}
                />
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      )}
    </>
  );
};

interface TopFulfilledSuppliersProps {
  handleSupplierClick: (groupKey: string, isFromTopSuppliers: boolean) => void;
  onRequestPartConfirmation?: (groupKey: string) => void;
  pinnedSupplierIds: string[];
  setIsTopSuppliers: (count: number) => void;
  preFacetedPartNumbers: any[];
  typesenseInstantsearchAdapter: any;
  onSelectionClose: () => void;
  customerSupplierAssociations: string[];
  customerGeoPoint: { latitude: number; longitude: number };
  handleSelectListItem: (
    item: { _id: string; supplierName: string },
    supplierParts?: any[],
  ) => void;
  handleAddToSelectedSupplierBucket: (item: any, supplierParts?: any[]) => void;
  setTopSupplierHitMap: (map: Map<string, any[]>) => void;
  supplierDiscoveryCollectionName: string;
  preFacetedPartNumbersMap: Map<string, number>;
  totalRequestedQuantity: number;
  topSupplierHitMap: Map<string, any[]>;
  supplier_discovery_parts_limit: Number;
  selectedSuppliers?: any[];
  supplierCityMap: Map<string, string>;
  isEstimateOrder: boolean;
  offlinePartAvailabilityDiscrepanciesPerSupplierMap?: Map<
    string,
    OfflineSupplierEntry
  >;
  supplierInventoryDeltaQuantityMap?: Map<string, number>;
}

const TOP_SUPPLIER_CANDIDATE_PAGE_SIZE = 10;

export const TopFulfilledSuppliers = ({
  handleSupplierClick,
  onRequestPartConfirmation,
  pinnedSupplierIds,
  setIsTopSuppliers,
  preFacetedPartNumbers,
  typesenseInstantsearchAdapter,
  onSelectionClose,
  customerSupplierAssociations,
  customerGeoPoint,
  handleSelectListItem,
  handleAddToSelectedSupplierBucket,
  setTopSupplierHitMap,
  supplierDiscoveryCollectionName,
  preFacetedPartNumbersMap,
  totalRequestedQuantity,
  topSupplierHitMap,
  supplier_discovery_parts_limit,
  selectedSuppliers = [],
  supplierCityMap,
  isEstimateOrder,
  offlinePartAvailabilityDiscrepanciesPerSupplierMap,
  supplierInventoryDeltaQuantityMap,
}: TopFulfilledSuppliersProps) => {
  const [typesenseHits, setTypesenseHits] = useState<Hit[]>([]);

  const buildTopSupplierCandidateState = useCallback(
    (topSupplierTypesenseHits: Hit[]) => {
      const selectedSupplierIds = new Set(
        selectedSuppliers.map((s) => s.supplierId),
      );
      const typesenseGroupKeys = new Set(
        topSupplierTypesenseHits.map((h) => h.group_key[0]).filter(Boolean),
      );

      type Candidate = {
        groupKey: string;
        hits: any[];
        found: number;
        isOfflineOnly: boolean;
      };
      const candidates: Candidate[] = [];
      if (
        offlinePartAvailabilityDiscrepanciesPerSupplierMap &&
        offlinePartAvailabilityDiscrepanciesPerSupplierMap.size > 0
      ) {
        Array.from(
          offlinePartAvailabilityDiscrepanciesPerSupplierMap.entries(),
        ).forEach(([, entry]) => {
          if (selectedSupplierIds.has(entry.supplierId)) return;
          if (typesenseGroupKeys.has(entry.groupKey)) return;

          const synthetic = createSyntheticTopHit(entry);
          const deltaAdjustedParts = applySupplierInventoryDeltasToParts({
            supplierId: entry.supplierId,
            parts: synthetic.hits,
            supplierInventoryDeltaQuantityMap,
          });
          candidates.push({
            groupKey: entry.groupKey,
            hits: deltaAdjustedParts,
            found: deltaAdjustedParts.length,
            isOfflineOnly: true,
          });
        });
      }

      for (const tsHit of topSupplierTypesenseHits) {
        const groupKey = tsHit.group_key[0];
        if (!groupKey) continue;
        const [, supplierId] = groupKey.split("::");
        if (selectedSupplierIds.has(supplierId)) continue;

        const offlineEntry =
          offlinePartAvailabilityDiscrepanciesPerSupplierMap?.get(groupKey);
        const mergedParts = offlineEntry
          ? mergeOfflinePartsIntoHits(
              tsHit.hits,
              offlineEntry.parts as Array<{
                rootPartNumber: string;
                familyQuantity: number;
              }>,
              offlineEntry.updatedAt,
            )
          : tsHit.hits;
        const deltaAdjustedParts = applySupplierInventoryDeltasToParts({
          supplierId,
          parts: mergedParts,
          supplierInventoryDeltaQuantityMap,
        });

        candidates.push({
          groupKey,
          hits: deltaAdjustedParts,
          found: deltaAdjustedParts.length,
          isOfflineOnly: false,
        });
      }

      const qualified = candidates.filter((c) =>
        isTopQualifiedFromParts(c.hits, preFacetedPartNumbersMap),
      );
      const top3 = qualified.slice(0, 3);

      const hitsResult: Hit[] = top3.map(({ groupKey, hits: h, found }) => ({
        group_key: [groupKey],
        hits: h,
        found,
      }));
      const offlineOnly = new Set(
        top3.filter((t) => t.isOfflineOnly).map((t) => t.groupKey),
      );

      const finalTopSupplierHitMap = new Map<string, any[]>();
      top3.forEach(({ groupKey, hits: h }) =>
        finalTopSupplierHitMap.set(groupKey, h),
      );

      return {
        hits: hitsResult,
        offlineOnlyTopGroupKeys: offlineOnly,
        finalTopSupplierHitMap,
        qualifiedTopSupplierCandidates: qualified,
      };
    },
    [
      offlinePartAvailabilityDiscrepanciesPerSupplierMap,
      selectedSuppliers,
      preFacetedPartNumbersMap,
      supplierInventoryDeltaQuantityMap,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    const fetchTopFulfilledSuppliersData = async () => {
      try {
        const selectedSupplierIds = selectedSuppliers.map(
          (supplier) => supplier.supplierId,
        );
        const accumulatedTopSupplierCandidates: Hit[] = [];
        const seenTopSupplierIds = new Set<string>();
        let nextTopSupplierPage = 1;
        let totalMatchingSupplierGroups = 0;

        while (!cancelled) {
          const { supplierCandidateHits, totalMatchingSupplierGroups: total } =
            await fetchTopSupplierCandidates({
              typesenseAdapter: typesenseInstantsearchAdapter,
              supplierDiscoveryCollectionName,
              preFacetedPartNumbers,
              customerGeoPoint,
              supplier_discovery_parts_limit,
              selectedSupplierIds,
              perPage: TOP_SUPPLIER_CANDIDATE_PAGE_SIZE,
              page: nextTopSupplierPage,
            });

          totalMatchingSupplierGroups = total;

          for (const supplierCandidateHit of supplierCandidateHits) {
            const groupKey = supplierCandidateHit.group_key?.[0];
            if (!groupKey) continue;
            const [, supplierId] = groupKey.split("::");
            if (!supplierId || seenTopSupplierIds.has(supplierId)) continue;

            seenTopSupplierIds.add(supplierId);
            accumulatedTopSupplierCandidates.push(supplierCandidateHit);
          }

          const { qualifiedTopSupplierCandidates } =
            buildTopSupplierCandidateState(accumulatedTopSupplierCandidates);

          const fetchedSupplierGroupCount =
            nextTopSupplierPage * TOP_SUPPLIER_CANDIDATE_PAGE_SIZE;
          const hasFetchedAllMatchingSupplierGroups =
            totalMatchingSupplierGroups > 0 &&
            fetchedSupplierGroupCount >= totalMatchingSupplierGroups;

          if (
            qualifiedTopSupplierCandidates.length >= 3 ||
            supplierCandidateHits.length === 0 ||
            hasFetchedAllMatchingSupplierGroups
          ) {
            break;
          }

          nextTopSupplierPage += 1;
        }

        if (!cancelled) setTypesenseHits(accumulatedTopSupplierCandidates);
      } catch (error) {
        console.error("Error fetching top suppliers:", error);
        if (!cancelled) setTypesenseHits([]);
      }
    };
    fetchTopFulfilledSuppliersData();
    return () => {
      cancelled = true;
    };
  }, [
    preFacetedPartNumbers,
    typesenseInstantsearchAdapter,
    supplierDiscoveryCollectionName,
    customerGeoPoint,
    selectedSuppliers,
    supplier_discovery_parts_limit,
    buildTopSupplierCandidateState,
  ]);

  const { hits, offlineOnlyTopGroupKeys, finalTopSupplierHitMap } =
    useMemo(
      () => buildTopSupplierCandidateState(typesenseHits),
      [typesenseHits, buildTopSupplierCandidateState],
    );

  useEffect(() => {
    setTopSupplierHitMap(finalTopSupplierHitMap);
  }, [finalTopSupplierHitMap, setTopSupplierHitMap]);

  return (
    <TopFulfilledSupplierList
      handleSupplierClick={handleSupplierClick}
      onRequestPartConfirmation={onRequestPartConfirmation}
      setIsTopSuppliers={setIsTopSuppliers}
      preFacetedPartNumbers={preFacetedPartNumbers}
      onSelectionClose={onSelectionClose}
      customerSupplierAssociations={customerSupplierAssociations}
      customerGeoPoint={customerGeoPoint}
      handleSelectListItem={handleSelectListItem}
      handleAddToSelectedSupplierBucket={handleAddToSelectedSupplierBucket}
      setTopSupplierHitMap={setTopSupplierHitMap}
      typesenseInstantsearchAdapter={typesenseInstantsearchAdapter}
      supplierDiscoveryCollectionName={supplierDiscoveryCollectionName}
      pinnedSupplierIds={pinnedSupplierIds}
      preFacetedPartNumbersMap={preFacetedPartNumbersMap}
      totalRequestedQuantity={totalRequestedQuantity}
      hits={hits}
      topSupplierHitMap={topSupplierHitMap}
      supplierCityMap={supplierCityMap}
      isEstimateOrder={isEstimateOrder}
      offlineOnlyTopGroupKeys={offlineOnlyTopGroupKeys}
    />
  );
};
